// Packing Lists (as v2.0.41): garment rows are carton-ratio groups (pieces per size in one carton × number of
// cartons); fabric rows are roll groups (metres per roll × number of rolls). Carton and roll numbers run on through
// the list. The list is reconciled against what the shipment carries of each line (total and per size). Shared by
// the server (which stores and checks it) and the screen (which recalculates while typing).

export const PACKING_STATUSES = [["draft", "Draft"], ["submitted", "Submitted"], ["final", "Final"]] as const;
export type PackingStatus = (typeof PACKING_STATUSES)[number][0];
export const PACKING_STATUS_LABEL = Object.fromEntries(PACKING_STATUSES) as Record<PackingStatus, string>;

/** A shipment line as the packing list sees it: what this shipment carries of it. */
export interface PackLine { lineId: string; code: string; description: string; colour: string; fabric: boolean; sizes: string[]; qtyBySize: Record<string, number>; total: number }
export interface PackRow {
  id: string; lineId: string; ratioBySize: Record<string, number>; cartons: number; rolls: number; metresPerRoll: number;
  pcsPerCarton: number; packedQty: number; cartonFrom: number; cartonTo: number; rollFrom: number; rollTo: number;
  netKg: number; grossKg: number; cbm: number; shippingMark: string; notes: string;
}

const num = (v: unknown, min = 0) => Math.max(min, Number(v) || 0);
const int = (v: unknown) => Math.max(0, Math.floor(Number(v) || 0));
const r2 = (n: number) => Math.round(n * 100) / 100;
const r3 = (n: number) => Math.round(n * 1000) / 1000;
let seq = 0;
export const newRowId = () => `r${Date.now().toString(36)}${(seq++).toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** Clean one row against its line (unknown sizes dropped, whole cartons and rolls, packed quantity worked out). */
export function normalizeRow(line: PackLine, x: any = {}): PackRow {
  const ratio = x.ratioBySize && typeof x.ratioBySize === "object" ? x.ratioBySize : {};
  const ratioBySize = line.fabric ? {} : Object.fromEntries(line.sizes.map((s) => [s, int(ratio[s])]));
  const cartons = line.fabric ? 0 : int(x.cartons), rolls = line.fabric ? int(x.rolls) : 0, metresPerRoll = line.fabric ? r2(num(x.metresPerRoll)) : 0;
  const pcsPerCarton = line.fabric ? 0 : Object.values(ratioBySize).reduce((a, b) => a + b, 0);
  return {
    id: String(x.id || newRowId()).slice(0, 40), lineId: line.lineId, ratioBySize, cartons, rolls, metresPerRoll, pcsPerCarton,
    packedQty: line.fabric ? r2(rolls * metresPerRoll) : cartons * pcsPerCarton, cartonFrom: 0, cartonTo: 0, rollFrom: 0, rollTo: 0,
    netKg: r2(num(x.netKg)), grossKg: r2(num(x.grossKg)), cbm: r3(num(x.cbm)),
    shippingMark: String(x.shippingMark || "").slice(0, 500), notes: String(x.notes || "").slice(0, 1000),
  };
}
export const emptyRow = (line: PackLine) => normalizeRow(line, {});

/** Carton numbers (1–n) and roll numbers run on through the list in order. */
export function assignRanges(rows: PackRow[], lines: PackLine[]): PackRow[] {
  let carton = 1, roll = 1;
  const fabric = new Set(lines.filter((l) => l.fabric).map((l) => l.lineId));
  return rows.map((r) => {
    if (fabric.has(r.lineId)) {
      const from = r.rolls ? roll : 0, to = r.rolls ? from + r.rolls - 1 : 0;
      if (r.rolls) roll = to + 1;
      return { ...r, rollFrom: from, rollTo: to, cartonFrom: 0, cartonTo: 0 };
    }
    const from = r.cartons ? carton : 0, to = r.cartons ? from + r.cartons - 1 : 0;
    if (r.cartons) carton = to + 1;
    return { ...r, cartonFrom: from, cartonTo: to, rollFrom: 0, rollTo: 0 };
  });
}

/** Rows kept in line order, every line with at least one row, ranges numbered. */
export function normalizeRows(lines: PackLine[], input: unknown): PackRow[] {
  const src = Array.isArray(input) ? input : [];
  const out: PackRow[] = [];
  for (const l of lines) {
    const mine = src.filter((x: any) => x && x.lineId === l.lineId).map((x) => normalizeRow(l, x));
    out.push(...(mine.length ? mine : [emptyRow(l)]));
  }
  return assignRanges(out, lines);
}

export function totals(rows: PackRow[], lines: PackLine[]) {
  const fabric = new Set(lines.filter((l) => l.fabric).map((l) => l.lineId));
  const g = rows.filter((r) => !fabric.has(r.lineId)), f = rows.filter((r) => fabric.has(r.lineId));
  return {
    cartons: g.reduce((a, r) => a + r.cartons, 0), pieces: g.reduce((a, r) => a + r.packedQty, 0),
    rolls: f.reduce((a, r) => a + r.rolls, 0), metres: r2(f.reduce((a, r) => a + r.packedQty, 0)),
    netKg: r2(rows.reduce((a, r) => a + r.netKg, 0)), grossKg: r2(rows.reduce((a, r) => a + r.grossKg, 0)), cbm: r3(rows.reduce((a, r) => a + r.cbm, 0)),
  };
}

/** Packed against what the shipment carries, per line and per size. */
export function lineCheck(line: PackLine, rows: PackRow[]) {
  const mine = rows.filter((r) => r.lineId === line.lineId);
  const packed = r2(mine.reduce((a, r) => a + r.packedQty, 0));
  const packedBySize = Object.fromEntries(line.sizes.map((s) => [s, mine.reduce((a, r) => a + (r.ratioBySize[s] || 0) * r.cartons, 0)]));
  const sizeDiffs = Object.fromEntries(line.sizes.map((s) => [s, packedBySize[s] - (Number(line.qtyBySize[s]) || 0)]));
  const qtyOk = Math.abs(packed - line.total) < 0.001;
  const sizesOk = line.fabric || line.sizes.every((s) => sizeDiffs[s] === 0);
  return { lineId: line.lineId, expected: line.total, packed, diff: r2(packed - line.total), qtyOk, sizesOk, ok: qtyOk && sizesOk, packedBySize, sizeDiffs,
    cartons: mine.reduce((a, r) => a + r.cartons, 0), rolls: mine.reduce((a, r) => a + r.rolls, 0) };
}
export function reconcile(lines: PackLine[], rows: PackRow[]) {
  const details = lines.map((l) => lineCheck(l, rows));
  return { details, ok: details.length > 0 && details.every((d) => d.ok), mismatch: details.filter((d) => !d.ok) };
}
/** Plain-language variance lines, e.g. "CV-1: 20 pcs short (M 20 short) — packed 380 / shipment 400 pcs". */
export function mismatchText(lines: PackLine[], rows: PackRow[]): string[] {
  return reconcile(lines, rows).mismatch.map((d) => {
    const l = lines.find((x) => x.lineId === d.lineId)!, unit = l.fabric ? "m" : "pcs";
    let why = d.diff < 0 ? `${Math.abs(d.diff)} ${unit} short` : d.diff > 0 ? `${d.diff} ${unit} over` : "size ratio mismatch";
    const sizes = l.sizes.filter((s) => d.sizeDiffs[s]).map((s) => `${s} ${d.sizeDiffs[s] < 0 ? `${-d.sizeDiffs[s]} short` : `${d.sizeDiffs[s]} over`}`);
    if (!l.fabric && sizes.length) why += ` (${sizes.join(", ")})`;
    return `${l.code}${l.colour ? ` ${l.colour}` : ""}: ${why} — packed ${d.packed} / shipment ${d.expected} ${unit}`;
  });
}
/** What stops a list from being submitted (every row needs cartons and a ratio, or rolls and metres). */
export function submitProblem(lines: PackLine[], rows: PackRow[]): string | null {
  if (!rows.length) return "The shipment has no packing rows.";
  const fabric = new Set(lines.filter((l) => l.fabric).map((l) => l.lineId));
  const bad = rows.find((r) => (fabric.has(r.lineId) ? r.rolls <= 0 || r.metresPerRoll <= 0 : r.cartons <= 0 || r.pcsPerCarton <= 0));
  return bad ? "Every packing row needs a number of cartons and pieces per carton (or rolls and metres per roll). Remove empty rows first." : null;
}
